import React, { useEffect, useState } from 'react';
import {
  Badge, Button, DataTable, IdCell, KV, Modal, PageHead, SearchBox, StatCard,
  Tabs, Textarea, dateStr, inr, num,
} from '../components/ui';
import useList from '../hooks/useList';
import { authorizationApi } from '../api';
import { useToast } from '../context/ToastContext';

const TABS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'revoked', label: 'Revoked' },
];

const Authorizations = () => {
  const toast = useToast();
  const list = useList(authorizationApi.list, { status: '', search: '' });

  const [stats, setStats] = useState(null);
  const [detail, setDetail] = useState(null);
  const [revoke, setRevoke] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const loadStats = () => authorizationApi.stats().then(res => setStats(res.data)).catch(() => {});
  useEffect(() => { loadStats(); }, []);

  const doRevoke = async () => {
    setBusy(true);
    try {
      await authorizationApi.revoke(revoke._id, reason);
      toast.success('Authorization revoked');
      setRevoke(null);
      setReason('');
      setDetail(null);
      list.reload();
      loadStats();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { key: 'requestNumber', label: 'Request', render: row => <span className="mono text-sm cell-strong">{row.requestNumber}</span> },
    {
      key: 'applicant',
      label: 'Applicant',
      render: row => <IdCell image={row.applicant?.logo} name={row.applicant?.name} sub={row.applicant?.code} />,
    },
    {
      key: 'manufacturer',
      label: 'Manufacturer',
      render: row => (
        <>
          <div>{row.manufacturer?.name}</div>
          <div className="cell-muted mono">{row.manufacturerCode}</div>
        </>
      ),
    },
    { key: 'requestedRole', label: 'As', render: row => <Badge status={row.requestedRole} tone="brand" dot={false} /> },
    { key: 'createdAt', label: 'Applied', render: row => <span className="text-sm">{dateStr(row.createdAt)}</span> },
    { key: 'totalBusiness', label: 'Business', align: 'right', render: row => inr(row.totalBusiness, true) },
    { key: 'status', label: 'Status', render: row => <Badge status={row.status} /> },
  ];

  return (
    <>
      <PageHead
        title="Authorizations"
        subtitle="Dealer and distributor applications to manufacturers, across the whole network."
      />

      {stats && (
        <div className="stat-grid stagger mb-2">
          <StatCard accent label="Total requests" value={num(stats.total)} icon="⛨" />
          <StatCard label="Pending review" value={num(stats.pending)} icon="⏳" foot="With manufacturers" />
          <StatCard label="Approved" value={num(stats.approved)} icon="✓" />
          <StatCard label="Rejected" value={num(stats.rejected)} icon="✕" />
          <StatCard label="Revoked" value={num(stats.revoked)} icon="⊘" />
        </div>
      )}

      <Tabs tabs={TABS} value={list.filters.status} onChange={v => list.setFilter('status', v)} />

      <div className="filters">
        <SearchBox value={list.filters.search} onChange={v => list.setFilter('search', v)} placeholder="Search request number or company…" />
        <div className="grow" />
        <span className="text-muted text-sm">{list.meta ? `${num(list.meta.total)} requests` : ''}</span>
      </div>

      <DataTable
        columns={columns}
        rows={list.rows}
        loading={list.loading}
        meta={list.meta}
        onPage={list.setPage}
        onRowClick={async row => {
          try {
            setDetail((await authorizationApi.get(row._id)).data);
          } catch (err) {
            toast.error(err.message);
          }
        }}
      />

      <Modal
        open={!!detail}
        size="wide"
        title={detail?.requestNumber}
        subtitle={detail ? `${detail.applicant?.name} → ${detail.manufacturer?.name}` : ''}
        onClose={() => setDetail(null)}
        footer={
          detail?.status === 'approved' && (
            <Button variant="danger" onClick={() => setRevoke(detail)}>Revoke authorization</Button>
          )
        }
      >
        {detail && (
          <>
            <div className="row mb-2"><Badge status={detail.status} /></div>

            <KV
              items={[
                { label: 'Applicant', value: `${detail.applicant?.name} (${detail.applicant?.code})` },
                { label: 'Applicant GSTIN', value: detail.applicant?.gstNumber || '—' },
                { label: 'Contact', value: detail.applicantUser ? `${detail.applicantUser.name} · ${detail.applicantUser.mobile}` : '—' },
                { label: 'Manufacturer', value: `${detail.manufacturer?.name} (${detail.manufacturerCode})` },
                { label: 'Applied as', value: detail.requestedRole },
                { label: 'Applied on', value: dateStr(detail.createdAt) },
                detail.reviewedAt && { label: 'Reviewed on', value: dateStr(detail.reviewedAt) },
                detail.territories?.length && { label: 'Territories', value: detail.territories.join(', ') },
                detail.discountPercent ? { label: 'Dealer discount', value: `${detail.discountPercent}%` } : null,
                detail.validTill && { label: 'Valid till', value: dateStr(detail.validTill) },
                { label: 'Orders placed', value: num(detail.totalOrders) },
                { label: 'Business done', value: inr(detail.totalBusiness) },
                detail.message && { label: 'Applicant note', value: detail.message },
                detail.remarks && { label: 'Reviewer remarks', value: detail.remarks },
              ]}
            />

            {(detail.documents || []).length > 0 && (
              <>
                <div className="divider" />
                <div className="card-title mb-2">Submitted documents</div>
                <div className="row wrap">
                  {detail.documents.map(doc => (
                    <a key={doc._id || doc.url} href={doc.url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                      📎 {doc.name || doc.type}
                    </a>
                  ))}
                </div>
              </>
            )}

            <div className="hint mt-2">
              Approve/reject decisions are made by the manufacturer in their own panel. As Super Admin you can
              audit any request here and revoke an approval if needed.
            </div>
          </>
        )}
      </Modal>

      <Modal
        open={!!revoke}
        title="Revoke this authorization?"
        onClose={() => setRevoke(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRevoke(null)}>Cancel</Button>
            <Button variant="danger" loading={busy} onClick={doRevoke}>Revoke</Button>
          </>
        }
      >
        <p className="text-muted mb-2">
          The dealer immediately loses access to this manufacturer's restricted products. Both parties are notified.
        </p>
        <Textarea label="Reason" value={reason} onChange={e => setReason(e.target.value)} placeholder="Territory agreement ended" />
      </Modal>
    </>
  );
};

export default Authorizations;
